import { FieldAiConfigView, FieldResponseStats, FormField } from "generated/graphql";
import { P } from "theme/typography";
import { Box, Chip } from "@mui/material";
import { AutoAwesome, Repeat, Rule, Settings } from "@mui/icons-material";
import { useAppSelector } from "redux/hooks";
import { selectLensEditorReviewConfigById, selectProfileBuilderAiMode } from "redux/slices/lensEditor";
import { AiFieldPreview } from "./AiFieldPreview";
import DescriptionTooltip from "components/FormField/DescriptionTooltip";

type FieldPreviewProps = {
  field: FormField;
  onClick?: () => void;
  fieldStats?: FieldResponseStats;
  aiFieldConfig?: FieldAiConfigView;
};

const FieldPreview = ({ field, onClick, fieldStats, aiFieldConfig }: FieldPreviewProps) => {
  const reviewConfig = useAppSelector((state) => selectLensEditorReviewConfigById(state, field.reviewConfigId || 0));
  const aiMode = useAppSelector(selectProfileBuilderAiMode);
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        cursor: onClick ? "pointer" : "default",
      }}>
      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}>
          <P variant="xlBold">{field.name}</P>
          <DescriptionTooltip description={field.description} />
        </Box>

        <P variant="subtitle2">{field.prompt}</P>
        <P variant="body3">
          {field.cells.length} cells
          {fieldStats && (
            <>
              , {(fieldStats?.totalResponses ?? 0).toLocaleString()} responses across{" "}
              {(fieldStats?.distinctCases ?? 0).toLocaleString()} cases
            </>
          )}
        </P>
      </Box>
      <Box
        sx={{
          "& > * + *": {
            marginLeft: 1,
          },
          // top margin only if promptYesNo or repeating
          mt: field.promptYesNo || field.repeating || reviewConfig ? 1 : 0,
        }}>
        {aiFieldConfig && (
          <Chip
            color="primary"
            label="AI Enabled"
            size="small"
            icon={<AutoAwesome />}
            sx={{
              pl: 0.5,
            }}
          />
        )}
        {reviewConfig && (
          <Chip
            label={`Review Config: ${reviewConfig?.name}`}
            size="small"
            icon={<Settings />}
            sx={{
              pl: 0.5,
            }}
          />
        )}
        {field.promptYesNo && (
          <Chip
            label="Prompt Yes/No"
            size="small"
            icon={<Rule />}
            sx={{
              pl: 0.5,
            }}
          />
        )}
        {field.repeating && (
          <Chip
            label="Repeating"
            size="small"
            icon={<Repeat />}
            sx={{
              pl: 0.5,
            }}
          />
        )}
      </Box>
      {aiMode && aiFieldConfig && <AiFieldPreview aiFieldConfig={aiFieldConfig} />}
    </Box>
  );
};

export default FieldPreview;
